import React, { ReactNode } from 'react';
import { Colors } from 'works/legacy/lib/style-helper';
import { TextOverflow } from '@common/components';
import { getNumberPositionSufix } from '@approvals/services/approvals-service';
import {
  ApprovalChainApproverTemplateType,
  ApprovalChainStepTemplateType,
  ApprovalTableRowModelType,
} from '@approvals/graphql';

function renderShortenName(name: string) {
  const namePieces = name.split(' ');
  const lastName = namePieces[namePieces.length - 1];
  const firstLetterFirstName = namePieces[0].substring(0, 1);

  return `${firstLetterFirstName}. ${lastName}`;
}

function renderJoinedApprovers(
  approvers?: ApprovalChainApproverTemplateType[]
) {
  const serializedNames =
    approvers?.reduce((acc, approver, i) => {
      const shortenName = renderShortenName(approver.worksMembership.name);
      return `${acc}${i !== 0 ? ' / ' : ''}${shortenName}`;
    }, '') || '';

  return <>{serializedNames}</>;
}

function renderStepSign(stepNumber: number) {
  const position = getNumberPositionSufix(stepNumber);

  return <span className="approvals-page__step-sign">{position}</span>;
}

function renderStepTooltipContent(
  approvers: ApprovalChainApproverTemplateType[],
  order: number
) {
  return (
    <ul className="approvals-page__tooltip-step-list">
      {approvers.map((approver, i: number) => {
        return (
          <li key={approver.id}>
            {i === 0 && (
              <>
                <b>{getNumberPositionSufix(order)} Level:</b>
                <br />
              </>
            )}
            {approver.worksMembership.name}
          </li>
        );
      })}
    </ul>
  );
}

function renderStepItem(
  order: number,
  approvers: ApprovalChainApproverTemplateType[]
) {
  return (
    <span>
      {renderStepSign(order)} {renderJoinedApprovers(approvers)}
    </span>
  );
}

function renderStepItems(
  step: ApprovalChainStepTemplateType,
  stepIndex: number
) {
  const order = stepIndex + 1;
  const approvers = step?.approvalChainApproverTemplates;

  return (
    <li key={step.id} className="approvals-page__step-item">
      <TextOverflow tooltip={renderStepTooltipContent(approvers, order)}>
        {renderStepItem(order, approvers)}
      </TextOverflow>
    </li>
  );
}

function isFirstRowOfGroup(
  row: ApprovalTableRowModelType,
  transformedDataSource: ApprovalTableRowModelType[]
) {
  return (
    row === transformedDataSource.find((unit) => unit.unitId === row.unitId)
  );
}

function getGroupQuantity(
  row: ApprovalTableRowModelType,
  transformedDataSource: ApprovalTableRowModelType[]
) {
  return transformedDataSource.filter((unit) => unit.unitId === row.unitId)
    .length;
}

type RenderObjType = {
  children?: ReactNode;
  props: {
    className?: string;
    rowSpan?: number;
  };
};

export function getColumnRenderObj(
  row: ApprovalTableRowModelType,
  value?: ReactNode
) {
  const renderObj = {
    children: value,
    props: {},
  } as RenderObjType;

  if (row.odd) {
    renderObj.props.className = 'table-odd-row';
  }

  return renderObj;
}

export const renderValueWithRowSpan = (
  transformedDataSource: ApprovalTableRowModelType[]
) => {
  return (value: string, row: ApprovalTableRowModelType) => {
    const renderObj = getColumnRenderObj(row, value);

    if (isFirstRowOfGroup(row, transformedDataSource)) {
      const quantity = getGroupQuantity(row, transformedDataSource);

      renderObj.props.rowSpan = quantity;
    } else {
      renderObj.props.rowSpan = 0;
    }
    return renderObj;
  };
};

function getSteps(steps: ApprovalChainStepTemplateType[]) {
  return (
    <ul className="approvals-page__approval-chain-list sentry-mask">
      {steps.map((step, stepIndex) => renderStepItems(step, stepIndex))}
    </ul>
  );
}

export function renderApprovalChainColumn(index: number) {
  return (_: unknown, row: ApprovalTableRowModelType) => {
    const isFirstColumn = index === 0;
    const renderObj = getColumnRenderObj(row);

    const steps = row.approvalChainTemplates[index]?.approvalChainStepTemplates;
    const hasApprovers =
      steps && steps[0]?.approvalChainApproverTemplates?.length;

    if (hasApprovers) {
      renderObj.children = getSteps(steps);
      return renderObj;
    }

    if (!isFirstColumn) {
      return renderObj;
    }

    if (row.isPreApproved) {
      renderObj.children = <>Pre-approved</>;
    } else {
      renderObj.children = (
        <span style={{ color: Colors.midnightLighter }}>N/A</span>
      );
    }

    return renderObj;
  };
}
